import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  message, 
  Card, 
  Row, 
  Col,
  Tag,
  Typography,
  Popconfirm,
  Drawer,
  List,
  Avatar,
  Divider,
  Radio,
  Upload,
  Switch
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  EyeOutlined, 
  PhoneOutlined, 
  MailOutlined, 
  WechatOutlined,
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  TeamOutlined,
  CopyOutlined,
  UploadOutlined,
  DownloadOutlined,
  ReloadOutlined,
  LinkOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import api from '../../utils/axios';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AuthContext';
import ImageViewer from '../../components/ImageViewer';
import AttachmentUpload from '../../components/AttachmentUpload';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// 面试记录组件
const InterviewRecords = ({ leadId, leadName, leadPhone }) => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (leadId || leadName || leadPhone) {
      fetchInterviews();
    }
  }, [leadId, leadName, leadPhone]);

  const fetchInterviews = async () => {
    setLoading(true);
    try {
      // 优先通过lead_id直接查询，如果没有则通过姓名和手机号搜索
      let response;
      if (leadId) {
        // 直接通过线索ID查询面试记录
        console.log('🔍 通过线索ID查询面试记录:', leadId);
        response = await api.get(`/interviews`);
        const allInterviews = Array.isArray(response.data) ? response.data : [];
        console.log('📊 获取到所有面试记录:', allInterviews.length);
        
        // 过滤出属于当前线索的面试记录
        const filteredInterviews = allInterviews.filter(interview => 
          interview.lead_id === leadId
        );
        console.log('✅ 过滤后的面试记录:', filteredInterviews.length, '条');
        console.log('🔗 线索ID匹配结果:', filteredInterviews.map(i => ({ id: i.id, lead_id: i.lead_id, lead_name: i.lead_name })));
        
        setInterviews(filteredInterviews);
      } else {
        // 通过姓名或手机号搜索面试记录
        console.log('🔍 通过姓名/手机号搜索面试记录:', { leadName, leadPhone });
        const params = new URLSearchParams();
        if (leadName) params.append('search', leadName);
        if (leadPhone) params.append('search', leadPhone);
        
        response = await api.get(`/interviews?${params.toString()}`);
        const allInterviews = Array.isArray(response.data) ? response.data : [];
        console.log('📊 搜索到的面试记录:', allInterviews.length);
        
        // 过滤出属于当前线索的面试记录
        const filteredInterviews = allInterviews.filter(interview => 
          interview.lead_name === leadName ||
          interview.lead_phone === leadPhone
        );
        console.log('✅ 过滤后的面试记录:', filteredInterviews.length, '条');
        
        setInterviews(filteredInterviews);
      }
    } catch (error) {
      console.error('获取面试记录失败:', error);
      setInterviews([]);
    } finally {
      setLoading(false);
    }
  };

  // 跳转到面试管理界面并搜索指定面试记录
  const handleInterviewClick = (interviewId) => {
    navigate('/interviews', { 
      state: { 
        searchInterviewId: interviewId,
        highlightInterview: true
      }
    });
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>加载中...</div>;
  }

  if (interviews.length === 0) {
    return <Text type="secondary">暂无面试记录</Text>;
  }

  return (
    <div>
      {interviews.map((interview) => (
        <div key={interview.id} style={{ 
          border: '1px solid #f0f0f0', 
          borderRadius: '6px', 
          padding: '12px', 
          marginBottom: '8px',
          backgroundColor: '#fafafa'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text strong>面试记录ID: </Text>
              <Button 
                type="link" 
                size="small" 
                icon={<LinkOutlined />}
                onClick={() => handleInterviewClick(interview.id)}
                style={{ 
                  padding: '0 8px', 
                  height: 'auto', 
                  color: '#1890ff',
                  textDecoration: 'underline'
                }}
              >
                {interview.id}
              </Button>
            </div>
            <Tag color={getInterviewStatusColor(interview.status)}>
              {getInterviewStatusText(interview.status)}
            </Tag>
          </div>
          <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
            <div><strong>面试职位：</strong>{interview.position || interview.position_name || '-'}</div>
            <div><strong>面试部门：</strong>{interview.department_name || '-'}</div>
            <div><strong>面试官：</strong>{interview.interviewer || '-'}</div>
            <div><strong>面试时间：</strong>{interview.interview_date ? dayjs(interview.interview_date).format('YYYY-MM-DD HH:mm') : '-'}</div>
            {interview.notes && <div><strong>备注：</strong>{interview.notes}</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

// 入职记录组件
const OnboardingRecords = ({ leadId, leadName, leadPhone }) => {
  const [onboardings, setOnboardings] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (leadId || leadName || leadPhone) {
      fetchOnboardings();
    }
  }, [leadId, leadName, leadPhone]);

  const fetchOnboardings = async () => {
    setLoading(true);
    try {
      // 优先通过lead_id直接查询，如果没有则通过姓名搜索
      let response;
      if (leadId) {
        // 直接通过线索ID查询入职记录
        console.log('🔍 通过线索ID查询入职记录:', leadId);
        response = await api.get(`/onboarding`);
        const allOnboardings = response.data?.onboardings || response.data || [];
        console.log('📊 获取到所有入职记录:', allOnboardings.length);
        
        // 过滤出属于当前线索的入职记录
        const filteredOnboardings = allOnboardings.filter(onboarding => 
          onboarding.lead_id === leadId
        );
        console.log('✅ 过滤后的入职记录:', filteredOnboardings.length, '条');
        console.log('🔗 线索ID匹配结果:', filteredOnboardings.map(o => ({ id: o.id, lead_id: o.lead_id, employee_name: o.employee_name })));
        
        setOnboardings(filteredOnboardings);
      } else {
        // 通过姓名搜索入职记录
        console.log('🔍 通过姓名搜索入职记录:', { leadName, leadPhone });
        const params = new URLSearchParams();
        if (leadName) params.append('search', leadName);
        
        response = await api.get(`/onboarding?${params.toString()}`);
        const allOnboardings = response.data?.onboardings || response.data || [];
        console.log('📊 搜索到的入职记录:', allOnboardings.length);
        
        // 过滤出属于当前线索的入职记录
        const filteredOnboardings = allOnboardings.filter(onboarding => 
          onboarding.employee_name === leadName ||
          (onboarding.phone === leadPhone && leadPhone) ||
          (onboarding.email && leadPhone && onboarding.email.includes('@'))
        );
        console.log('✅ 过滤后的入职记录:', filteredOnboardings.length, '条');
        
        setOnboardings(filteredOnboardings);
      }
    } catch (error) {
      console.error('获取入职记录失败:', error);
      setOnboardings([]);
    } finally {
      setLoading(false);
    }
  };

  // 跳转到入职管理界面并搜索指定入职记录
  const handleOnboardingClick = (onboardingId) => {
    navigate('/onboarding', { 
      state: { 
        searchOnboardingId: onboardingId,
        highlightOnboarding: true
      }
    });
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '20px' }}>加载中...</div>;
  }

  if (onboardings.length === 0) {
    return <Text type="secondary">暂无入职记录</Text>;
  }

  return (
    <div>
      {onboardings.map((onboarding) => (
        <div key={onboarding.id} style={{ 
          border: '1px solid #f0f0f0', 
          borderRadius: '6px', 
          padding: '12px', 
          marginBottom: '8px',
          backgroundColor: '#fafafa'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text strong>入职记录ID: </Text>
              <Button 
                type="link" 
                size="small" 
                icon={<LinkOutlined />}
                onClick={() => handleOnboardingClick(onboarding.id)}
                style={{ 
                  padding: '0 8px', 
                  height: 'auto', 
                  color: '#1890ff',
                  textDecoration: 'underline'
                }}
              >
                {onboarding.id}
              </Button>
            </div>
            <Tag color={getOnboardingStatusColor(onboarding.status)}>
              {getOnboardingStatusText(onboarding.status)}
            </Tag>
          </div>
          <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
            <div><strong>员工姓名：</strong>{onboarding.employee_name || '-'}</div>
            <div><strong>入职职位：</strong>{onboarding.position || '-'}</div>
            <div><strong>入职部门：</strong>{onboarding.department || '-'}</div>
            <div><strong>入职日期：</strong>{onboarding.start_date ? dayjs(onboarding.start_date).format('YYYY-MM-DD') : '-'}</div>
            {onboarding.notes && <div><strong>备注：</strong>{onboarding.notes}</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

// 获取面试状态颜色
const getInterviewStatusColor = (status) => {
  switch (status) {
    case 'pending': return 'blue';
    case 'completed': return 'green';
    case 'no_show': return 'red';
    case 'suitable': return 'green';
    case 'not_suitable': return 'red';
    default: return 'default';
  }
};

// 获取面试状态文本
const getInterviewStatusText = (status) => {
  switch (status) {
    case 'pending': return '待面试';
    case 'completed': return '已完成';
    case 'no_show': return '爽约';
    case 'suitable': return '合适';
    case 'not_suitable': return '不合适';
    default: return status || '未知';
  }
};

// 获取入职状态颜色
const getOnboardingStatusColor = (status) => {
  switch (status) {
    case 'pending': return 'blue';
    case 'in_progress': return 'orange';
    case 'completed': return 'green';
    case 'cancelled': return 'red';
    default: return 'default';
  }
};

// 获取入职状态文本
const getOnboardingStatusText = (status) => {
  switch (status) {
    case 'pending': return '待开始';
    case 'in_progress': return '进行中';
    case 'completed': return '已完成';
    case 'cancelled': return '已取消';
    default: return status || '未知';
  }
};

const PublicLeads = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentLead, setCurrentLead] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [form] = Form.useForm();
  const [followUpForm] = Form.useForm();
  const [ratings, setRatings] = useState([]);
  const [tags, setTags] = useState([]);
  const [sources, setSources] = useState([]);
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [imageViewerVisible, setImageViewerVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState({ url: '', name: '', downloadUrl: '' });
  const [showSystemOperations, setShowSystemOperations] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importForm] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchClaimLoading, setBatchClaimLoading] = useState(false);

  // 打开图片预览
  const openImageViewer = (imageUrl, imageName, downloadUrl) => {
    if (imageUrl) {
      setCurrentImage({ 
        url: imageUrl, 
        name: imageName || '图片', 
        downloadUrl: downloadUrl || imageUrl 
      });
      setImageViewerVisible(true);
    }
  };

  // 关闭图片预览
  const closeImageViewer = () => {
    setImageViewerVisible(false);
    setCurrentImage({ url: '', name: '', downloadUrl: '' });
  };

  const [filters, setFilters] = useState({
    search: '',
    id: '',
    tags: [],
    gender: '',
    status: '',
    rating_id: '',
    source_id: '',
    created_today: false,
    age_min: '',
    age_max: '',
    sort_by: 'created_at',
    sort_dir: 'desc'
  });

  // 获取简历列表
  const fetchLeads = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.id) params.append('id', filters.id);
      if (filters.tags.length > 0) params.append('tags', filters.tags.join(','));
      if (filters.gender) params.append('gender', filters.gender);
      if (filters.rating_id) params.append('rating_id', filters.rating_id);
      if (filters.source_id) params.append('source_id', filters.source_id);
      if (filters.created_today) params.append('created_today', 'true');
      if (filters.age_min) params.append('age_min', filters.age_min);
      if (filters.age_max) params.append('age_max', filters.age_max);
      // 显示所有未分配的简历（assigned_to 为 null 或 0），并排除"在职中"状态
      params.append('assigned_to', '0');
      params.append('exclude_status', 'active');
      // 排序
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.sort_dir) params.append('sort_dir', filters.sort_dir);
      // 添加分页参数
      params.append('page', page);
      params.append('limit', pagination.pageSize);

      const response = await api.get(`/leads?${params}`);
      
      // 处理新的分页响应格式
      if (response.data && response.data.data) {
        setLeads(response.data.data);
        setPagination(response.data.pagination);
      } else {
        // 兼容旧格式
        setLeads(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('获取简历列表失败:', error);
      message.error(`获取简历列表失败: ${error.response?.data?.message || error.message}`);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  // 获取评级、标签和来源
  const fetchSettings = async () => {
    try {
      const [ratingsRes, tagsRes, sourcesRes] = await Promise.all([
        api.get('/settings/ratings'),
        api.get('/settings/tags'),
        api.get('/sources')
      ]);
      setRatings(Array.isArray(ratingsRes.data) ? ratingsRes.data : []);
      setTags(Array.isArray(tagsRes.data) ? tagsRes.data : []);
      setSources(Array.isArray(sourcesRes.data) ? sourcesRes.data : []);
    } catch (error) {
      console.error('获取设置失败:', error);
      message.error(`获取设置失败: ${error.response?.data?.message || error.message}`);
      setRatings([]);
      setTags([]);
      setSources([]);
    }
  };

  // 处理分页变化
  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }));
    fetchLeads(page);
  };

  useEffect(() => {
    fetchLeads();
    fetchSettings();
  }, [filters]);

  // 处理筛选变化
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // 清除所有筛选
  const handleClearAllFilters = () => {
    setFilters({
      search: '',
      id: '',
      tags: [],
      gender: '',
      status: '',
      rating_id: '',
      source_id: '',
      created_today: false,
      age_min: '',
      age_max: ''
    });
  };

  // 下载导入模板
  const handleDownloadTemplate = () => {
    const templateData = [
      {
        name: '张三',
        phone: '13800138000',
        email: 'zhangsan@example.com',
        wechat: 'zhangsan123',
        gender: 'male',
        age: '25',
        education: '本科',
        experience: '3年',
        expected_position: '前端开发',
        expected_salary: '15k-20k',
        source: '招聘网站',
        notes: '有React开发经验'
      },
      {
        name: '李四',
        phone: '13900139000',
        email: 'lisi@example.com',
        wechat: 'lisi456',
        gender: 'female',
        age: '28',
        education: '硕士',
        experience: '5年',
        expected_position: '产品经理',
        expected_salary: '20k-30k',
        source: '内部推荐',
        notes: '有BAT工作经验'
      }
    ];

    const csvContent = [
      '姓名,手机号,邮箱,微信,性别,年龄,学历,工作经验,期望职位,期望薪资,来源,备注',
      ...templateData.map(row => 
        `${row.name},${row.phone},${row.email},${row.wechat},${row.gender},${row.age},${row.education},${row.experience},${row.expected_position},${row.expected_salary},${row.source},${row.notes}`
      )
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', '简历导入模板.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 处理批量导入
  const handleImportSubmit = async (values) => {
    if (!values.file || values.file.length === 0) {
      message.error('请选择要导入的文件');
      return;
    }

    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', values.file[0].originFileObj);

      const response = await api.post('/leads/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success(`成功导入 ${response.data.imported} 条简历`);
      setImportModalVisible(false);
      importForm.resetFields();
      fetchLeads(pagination.current); // 保持当前页码
    } catch (error) {
      console.error('导入失败:', error);
      message.error(error.response?.data?.error || '导入简历失败');
    } finally {
      setImportLoading(false);
    }
  };

  // 添加线索
  const handleAdd = () => {
    form.resetFields();
    setModalVisible(true);
  };

  // 编辑线索
  const handleEdit = (record) => {
    setEditingLead(record);
    form.setFieldsValue({
      ...record
    });
    setModalVisible(true);
  };

  // 查看详情
  const handleView = async (record) => {
    setCurrentLead(record);
    setDrawerVisible(true);
    
    try {
      const [fuRes, attRes] = await Promise.all([
        api.get(`/leads/${record.id}/follow-ups`),
        api.get(`/upload/lead/${record.id}`)
      ]);
      setFollowUpHistory(fuRes.data || []);
      setAttachments(attRes.data?.data || []);
    } catch (error) {
      console.error('获取详情失败:', error);
      setFollowUpHistory([]);
      setAttachments([]);
    }
  };

  // 认领线索
  const handleClaim = async (record) => {
    try {
      await api.put(`/leads/${record.id}/claim`, {
        status: 'assigned',
        assigned_to: user.id
      });
      message.success('简历认领成功');
      fetchLeads(pagination.current); // 保持当前页码
    } catch (error) {
      console.error('认领线索失败:', error);
      message.error('认领简历失败');
    }
  };

  // 批量认领线索
  const handleBatchClaim = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要认领的简历');
      return;
    }

    setBatchClaimLoading(true);
    try {
      const promises = selectedRowKeys.map(id => 
        api.put(`/leads/${id}/claim`, {
          status: 'assigned',
          assigned_to: user.id
        })
      );
      
      await Promise.all(promises);
      message.success(`成功认领 ${selectedRowKeys.length} 条简历`);
      setSelectedRowKeys([]);
      fetchLeads(pagination.current); // 保持当前页码
    } catch (error) {
      console.error('批量认领失败:', error);
      message.error('批量认领简历失败');
    } finally {
      setBatchClaimLoading(false);
    }
  };

  // 单个删除线索（仅超级管理员）
  const handleDelete = async (record) => {
    try {
      await api.delete(`/leads/${record.id}`);
      message.success('删除成功');
      fetchLeads(pagination.current); // 保持当前页码
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  // 批量删除线索（仅超级管理员）
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的简历');
      return;
    }

    try {
      const response = await api.delete('/leads/batch', {
        data: { ids: selectedRowKeys }
      });
      message.success(`批量删除成功，共删除 ${response.data.deletedCount} 条简历`);
      setSelectedRowKeys([]);
      fetchLeads(pagination.current); // 保持当前页码
    } catch (error) {
      message.error(error.response?.data?.message || '批量删除失败');
    }
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      if (editingLead) {
        await api.put(`/leads/${editingLead.id}`, values);
        message.success('简历更新成功');
        
        // 如果当前正在查看这个线索的详情，刷新当前线索信息
        if (currentLead && currentLead.id === editingLead.id) {
          try {
            const updatedLeadResponse = await api.get(`/leads/${editingLead.id}`);
            setCurrentLead(updatedLeadResponse.data);
            
            // 同时更新线索列表中的对应项
            setLeads(prevLeads => 
              prevLeads.map(lead => 
                lead.id === editingLead.id ? updatedLeadResponse.data : lead
              )
            );
          } catch (error) {
            console.error('刷新线索详情失败:', error);
            // 如果刷新失败，仍然刷新整个列表
            fetchLeads(pagination.current);
          }
        } else {
          // 如果不在查看详情，只刷新列表
          fetchLeads(pagination.current);
        }
      } else {
        // 根据简历归属设置状态
        let status = values.status || 'non_employee';
        if (values.ownership === 'public') {
          // 如果是公海简历，设置为公海状态
          status = 'public';
        }
        const submitData = { ...values, status };
        const resp = await api.post('/leads', submitData);
        const newId = resp.data?.id;
        // 绑定上传会话中的文件到新简历（如果存在）
        if (window.__LAST_UPLOAD_ID__ && newId) {
          try {
            await api.post('/upload/bind', { uploadId: window.__LAST_UPLOAD_ID__, leadId: newId });
          } catch (e) { /* 忽略绑定失败 */ }
        }
        message.success('简历创建成功');
        fetchLeads(pagination.current);
      }
      setModalVisible(false);
    } catch (error) {
      console.error('保存线索失败:', error);
      console.error('错误详情:', error.response?.data);
      console.error('错误状态:', error.response?.status);
      message.error(`保存简历失败: ${error.response?.data?.message || error.message}`);
    }
  };

  // 添加跟进记录
  const handleFollowUpSubmit = async (values) => {
    try {
      let followUpContent = values.content;
      
      if (values.rating_id) {
        const rating = ratings.find(r => r.id === values.rating_id);
        followUpContent += `\n\n评级：${rating?.name || ''}`;
      }
      
      if (values.tags && values.tags.length > 0) {
        const selectedTags = Array.isArray(tags) ? tags.filter(tag => values.tags.includes(tag.id)) : [];
        followUpContent += `\n\n标签：${selectedTags.map(tag => tag.name).join(', ')}`;
      }
      
      if (values.next_contact_at) {
        followUpContent += `\n\n下次联系时间：${dayjs(values.next_contact_at).format('YYYY-MM-DD HH:mm')}`;
      }

      await api.post(`/leads/${currentLead.id}/follow-ups`, {
        content: followUpContent
      });

      message.success('跟进记录添加成功');
      followUpForm.resetFields();
      
      // 刷新跟进历史和当前线索数据
      try {
        const response = await api.get(`/leads/${currentLead.id}`);
        const updatedLead = response.data;
        
        // 更新跟进历史
        setFollowUpHistory(updatedLead.follow_ups || []);
        
        // 更新当前线索数据，确保包含所有必要字段
        setCurrentLead(prevLead => ({
          ...prevLead, // 保留原有数据
          ...updatedLead, // 更新新数据
          // 确保关键字段不为空
          name: updatedLead.name || prevLead.name,
          phone: updatedLead.phone || prevLead.phone,
          email: updatedLead.email || prevLead.email,
          wechat: updatedLead.wechat || prevLead.wechat,
          gender: updatedLead.gender || prevLead.gender,
          age: updatedLead.age || prevLead.age,
          education_level: updatedLead.education_level || prevLead.education_level,
          work_status: updatedLead.work_status || prevLead.work_status,
          work_years: updatedLead.work_years || prevLead.work_years,
          current_position: updatedLead.current_position || prevLead.current_position,
          current_company: updatedLead.current_company || prevLead.current_company,
          current_salary: updatedLead.current_salary || prevLead.current_salary,
          expected_position: updatedLead.expected_position || prevLead.expected_position,
          expected_salary: updatedLead.expected_salary || prevLead.expected_salary,
          province: updatedLead.province || prevLead.province,
          city: updatedLead.city || prevLead.city,
          address: updatedLead.address || prevLead.address,
          marital_status: updatedLead.marital_status || prevLead.marital_status,
          graduate_school: updatedLead.graduate_school || prevLead.graduate_school,
          major: updatedLead.major || prevLead.major,
          notes: updatedLead.notes || prevLead.notes,
          rating_id: updatedLead.rating_id || prevLead.rating_id,
          rating_name: updatedLead.rating_name || prevLead.rating_name,
          rating_color: updatedLead.rating_color || prevLead.rating_color,
          tags: updatedLead.tags || prevLead.tags,
          source_id: updatedLead.source_id || prevLead.source_id,
          source_name: updatedLead.source_name || prevLead.source_name,
          status: updatedLead.status || prevLead.status,
          next_contact_at: updatedLead.next_contact_at || prevLead.next_contact_at
        }));
      } catch (error) {
        console.error('获取跟进历史失败:', error);
        // 如果获取失败，至少刷新跟进历史
        try {
          const followUpResponse = await api.get(`/leads/${currentLead.id}/follow-ups`);
          setFollowUpHistory(followUpResponse.data || []);
        } catch (followUpError) {
          console.error('获取跟进历史失败:', followUpError);
        }
      }
    } catch (error) {
      console.error('添加跟进记录失败:', error);
      message.error('添加跟进记录失败');
    }
  };

  // 复制联系方式
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${type}已复制到剪贴板`);
    }).catch(() => {
      message.error('复制失败');
    });
  };

  const renderFollowUpContent = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+|\/api\/upload\/download\/[A-Za-z0-9\-]+)/g;
    const tokens = text.split(urlRegex);
    return (
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {tokens.map((tok, idx) => {
          if (!tok) return null;
          if (urlRegex.test(tok)) {
            const img = (
              <img
                src={tok}
                alt="follow-up-img"
                style={{ width: '30%', maxWidth: 240, borderRadius: 6, marginTop: 6, display: 'block', cursor: 'zoom-in' }}
                onClick={() => openImageViewer(tok, '跟进图片', tok)}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            );
            return (
              <span key={idx} style={{ display: 'inline-block', maxWidth: '100%' }}>
                <a href={tok} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>{tok}</a>
                {img}
              </span>
            );
          }
          return <span key={idx}>{tok}</span>;
        })}
      </div>
    );
  };

  // 表格列定义
  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      render: (text, record) => (
        <div style={{ cursor: 'pointer' }} onClick={() => handleView(record)}>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>ID: {record.id}</div>
        </div>
      )
    },
    {
      title: '联系方式',
      key: 'contact',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ cursor: 'pointer', color: '#000' }} onClick={() => copyToClipboard(record.phone, '手机号')} title="点击复制手机号">
            手机：{record.phone}
          </div>
          <div 
            style={{ 
              cursor: record.wechat ? 'pointer' : 'default', 
              color: record.wechat ? '#000' : '#999' 
            }} 
            onClick={record.wechat ? () => copyToClipboard(record.wechat, '微信号') : undefined} 
            title={record.wechat ? "点击复制微信号" : ""}
          >
            微信：{record.wechat || '-'}
          </div>
        </div>
      )
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 80,
      render: (text) => text === 'male' ? '男' : text === 'female' ? '女' : '-'
    },
    {
      title: '年龄',
      dataIndex: 'age',
      key: 'age',
      width: 80,
      render: (text) => text ? `${text}岁` : '-'
    },
    {
      title: '期望职位',
      dataIndex: 'expected_position',
      key: 'expected_position',
      width: 150
    },
    {
      title: '评级',
      dataIndex: 'rating',
      key: 'rating',
      width: 100,
      render: (_, record) => {
        const rating = ratings.find(r => r.id === record.rating_id);
        return rating ? (
          <Tag color={rating.color}>{rating.name}</Tag>
        ) : '-';
      }
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 120,
      render: (_, record) => (
        <div>
          {Array.isArray(record.tags) && record.tags.map(tag => (
            <Tag key={tag.id} size="small">{tag.name}</Tag>
          ))}
        </div>
      )
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 100
    },
    {
      title: '时间信息',
      key: 'time_info',
      width: 200,
      render: (_, record) => (
        <div style={{ lineHeight: '1.4' }}>
          <div>创建: {dayjs(record.created_at).format('YYYY-MM-DD HH:mm')}</div>
          <div>联系: {record.last_follow_up_at ? dayjs(record.last_follow_up_at).format('YYYY-MM-DD HH:mm') : '-'}</div>
          <div>丢弃: {record.discarded_at ? dayjs(record.discarded_at).format('YYYY-MM-DD HH:mm') : '-'}</div>
        </div>
      )
    },
    {
      title: '添加人',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 120,
      render: (text, record) => {
        // 如果是从agent导入的简历，显示"系统导入"
        if (record.source === '简历导入') {
          return <Tag color="blue">系统导入</Tag>;
        }
        // 显示创建人姓名，如果没有则显示"-"
        return text || record.created_by_username || '-';
      }
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 200,
      align: 'right',
      render: (_, record) => (
        <Space size="small" style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => handleView(record)}
            size="small"
            title="查看详情"
          />

          <Button 
            type="primary" 
            size="small"
            title="认领简历"
            onClick={() => handleClaim(record)}
          >
            认领
          </Button>
          
          {/* 删除按钮（仅超级管理员可见） */}
          {user?.role === 'super_admin' && (
            <Button 
              type="link" 
              danger
              icon={<DeleteOutlined />} 
              onClick={() => handleDelete(record)}
              size="small"
              title="删除简历"
            />
          )}
        </Space>
      )
    }
  ];

  return (
    <main className="ant-layout-content css-l9pxc0">
      <div style={{ padding: '10px 16px' }}>
      {/* 筛选卡片 */}
      <Card 
        style={{ marginBottom: 16 }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>筛选条件</span>
            <Button 
              type="text" 
              onClick={handleClearAllFilters}
              size="small"
              style={{ padding: 0, fontSize: '12px' }}
            >
              清除筛选
            </Button>
          </div>
        }
      >
        <Row gutter={[0, 15]}>
          <Col span={24}>
            <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
              <Form.Item label="性别" style={{ marginBottom: 0, minWidth: '160px' }}>
                <Radio.Group
                  value={filters.gender}
                  onChange={(e) => handleFilterChange('gender', e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button value="">全部</Radio.Button>
                  <Radio.Button value="male">男</Radio.Button>
                  <Radio.Button value="female">女</Radio.Button>
                  <Radio.Button value="unknown">未知</Radio.Button>
                </Radio.Group>
              </Form.Item>
              <Form.Item label="时间" style={{ marginBottom: 0, minWidth: '160px' }}>
                <Radio.Group
                  value={filters.created_today}
                  onChange={(e) => handleFilterChange('created_today', e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button value={false}>不限</Radio.Button>
                  <Radio.Button value={true}>当天新增</Radio.Button>
                </Radio.Group>
              </Form.Item>
              <Form.Item label="排序" style={{ marginBottom: 0, minWidth: '240px' }}>
                <Space size="small">
                  <Select value={filters.sort_by} onChange={(v) => handleFilterChange('sort_by', v)} style={{ width: 160 }}>
                    <Option value="created_at">按创建时间</Option>
                    <Option value="last_follow_up_at">按联系时间</Option>
                    <Option value="discarded_at">按丢弃时间</Option>
                  </Select>
                  <Select value={filters.sort_dir} onChange={(v) => handleFilterChange('sort_dir', v)} style={{ width: 120 }}>
                    <Option value="desc">倒序</Option>
                    <Option value="asc">正序</Option>
                  </Select>
                </Space>
              </Form.Item>
            </div>
          </Col>
          <Col span={24}>
            <Form.Item label="来源" style={{ marginBottom: 0 }}>
              <div style={{ width: '80%' }}>
                <Radio.Group
                  value={filters.source_id}
                  onChange={(e) => handleFilterChange('source_id', e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button value="">全部</Radio.Button>
                  {Array.isArray(sources) && sources.map(source => (
                    <Radio.Button key={source.id} value={source.id}>
                      {source.name}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </div>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="评级" style={{ marginBottom: 0 }}>
              <div style={{ width: '80%' }}>
                <Radio.Group
                  value={filters.rating_id}
                  onChange={(e) => handleFilterChange('rating_id', e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button value="">全部</Radio.Button>
                  <Radio.Button value="unrated">未评级</Radio.Button>
                  {Array.isArray(ratings) && ratings.map(rating => (
                    <Radio.Button key={rating.id} value={rating.id}>
                      {rating.name}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </div>
            </Form.Item>
          </Col>
          <Col span={24}>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
              <Form.Item label="标签" style={{ marginBottom: 0, flex: '0 0 auto', minWidth: '200px' }}>
                <Select
                  mode="multiple"
                  placeholder="选择标签筛选"
                  value={filters.tags}
                  onChange={(value) => handleFilterChange('tags', value)}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {Array.isArray(tags) && tags.map(tag => (
                    <Option key={tag.id} value={tag.id}>{tag.name}</Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="年龄" style={{ marginBottom: 0, flex: '0 0 auto' }}>
                <Space size="small">
                  <Input
                    placeholder="最小"
                    style={{ width: 100 }}
                    value={filters.age_min}
                    onChange={(e) => handleFilterChange('age_min', e.target.value)}
                    type="number"
                    min="0"
                    max="100"
                  />
                  <span style={{ color: '#666' }}>至</span>
                  <Input
                    placeholder="最大"
                    style={{ width: 100 }}
                    value={filters.age_max}
                    onChange={(e) => handleFilterChange('age_max', e.target.value)}
                    type="number"
                    min="0"
                    max="100"
                  />
                </Space>
              </Form.Item>
              <Form.Item label="搜索" style={{ marginBottom: 0, flex: '0 0 auto', minWidth: '180px' }}>
                <Input
                  placeholder="姓名/手机号/微信"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  allowClear
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="ID筛选" style={{ marginBottom: 0, flex: '0 0 auto', minWidth: '150px' }}>
                <Input
                  placeholder="输入简历ID"
                  value={filters.id}
                  onChange={(e) => handleFilterChange('id', e.target.value)}
                  allowClear
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 简历公海主卡片 */}
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0 }}>简历公海</h1>
          <Space>
            {selectedRowKeys.length > 0 && (
              <>
                <Button 
                  type="primary"
                  loading={batchClaimLoading}
                  onClick={handleBatchClaim}
                >
                  批量认领简历 ({selectedRowKeys.length})
                </Button>
                {/* 批量删除按钮（仅超级管理员可见） */}
                {user?.role === 'super_admin' && (
                  <Button 
                    type="danger"
                    onClick={handleBatchDelete}
                  >
                    批量删除简历 ({selectedRowKeys.length})
                  </Button>
                )}
              </>
            )}
            <Button 
              icon={<ReloadOutlined />}
              onClick={() => fetchLeads(pagination.current)}
              loading={loading}
            >
              刷新
            </Button>
            <Button 
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
            >
              下载模板
            </Button>
            <Button 
              icon={<UploadOutlined />}
              onClick={() => setImportModalVisible(true)}
            >
              批量导入
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              添加简历
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={leads}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            getCheckboxProps: (record) => ({
              disabled: false // 允许所有公海简历都可以被勾选
            })
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange
          }}
        />
      </Card>

      {/* 批量导入模态框 */}
      <Modal
        title="批量导入简历"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={importForm}
          layout="vertical"
          onFinish={handleImportSubmit}
        >
          <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#f6f8fa', borderRadius: '6px' }}>
            <Title level={5} style={{ margin: 0, marginBottom: 8 }}>导入说明</Title>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              <li>请先下载模板，按照模板格式填写数据</li>
              <li>支持的文件格式：CSV、Excel (.xlsx, .xls)</li>
              <li>文件大小不超过5MB</li>
              <li>必填字段：姓名、手机号</li>
              <li>性别请填写：male(男)、female(女)、unknown(未知)</li>
            </ul>
          </div>

          <Form.Item
            name="file"
            label="选择文件"
            rules={[{ required: true, message: '请选择要导入的文件' }]}
          >
            <Upload
              accept=".csv,.xlsx,.xls"
              style={{ width: '80%' }}
              maxCount={1}
              beforeUpload={() => false}
              showUploadList={{
                showPreviewIcon: false,
                showRemoveIcon: true,
              }}
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setImportModalVisible(false)}>
                取消
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={importLoading}
              >
                开始导入
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加/编辑线索抽屉（与我的线索一致样式：右侧、竖排、窄宽度） */}
      <Drawer
        title={editingLead ? '编辑线索' : '添加线索'}
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        width={520}
        placement="right"
        destroyOnClose
        closable
        zIndex={1000}
        className="add-lead-drawer"
      >
        <style>{`.add-lead-drawer .ant-col{flex:0 0 100% !important;max-width:100% !important;}`}</style>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="ownership"
                label="线索归属"
                initialValue="public"
              >
                <Radio.Group buttonStyle="solid">
                  <Radio.Button value="public">公海</Radio.Button>
                  <Radio.Button value="private">私有</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="name"
                label="姓名"
                rules={[{ required: true, message: '请输入姓名' }]}
              >
                <Input placeholder="请输入姓名" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="gender"
                label="性别"
                initialValue="unknown"
              >
                <Select placeholder="请选择性别" allowClear>
                  <Option value="male">男</Option>
                  <Option value="female">女</Option>
                  <Option value="unknown">未知</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="phone"
                label="手机号"
                rules={[
                  { required: true, message: '请输入手机号' },
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }
                ]}
              >
                <Input placeholder="请输入手机号" disabled={!!editingLead} title={editingLead ? '手机号不可编辑' : ''} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="age" label="年龄">
                <Input placeholder="请输入年龄" type="number" min="16" max="100" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="wechat" label="微信">
                <Input placeholder="请输入微信号" />
              </Form.Item>
            </Col>
          </Row>

          {/* 保留原有其余字段 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="education_level" label="学历">
                <Select placeholder="请选择学历" allowClear>
                  <Option value="高中">高中</Option>
                  <Option value="大专">大专</Option>
                  <Option value="本科">本科</Option>
                  <Option value="硕士">硕士</Option>
                  <Option value="博士">博士</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="graduate_school" label="毕业院校">
                <Input placeholder="请输入毕业院校" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="major" label="专业">
                <Input placeholder="请输入专业" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="work_years" label="工作年限">
                <Input placeholder="请输入工作年限" type="number" min="0" max="50" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="work_status" label="工作状态">
                <Select placeholder="请选择工作状态" allowClear>
                  <Option value="在职">在职</Option>
                  <Option value="离职">离职</Option>
                  <Option value="待业">待业</Option>
                  <Option value="应届生">应届生</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="marital_status" label="婚姻状况">
                <Select placeholder="请选择婚姻状况" allowClear>
                  <Option value="未婚">未婚</Option>
                  <Option value="已婚">已婚</Option>
                  <Option value="离异">离异</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="current_position" label="当前职位">
                <Input placeholder="请输入当前职位" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="current_company" label="当前公司">
                <Input placeholder="请输入当前公司" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="current_salary" label="当前薪资">
                <Input placeholder="请输入当前薪资" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="expected_position" label="期望职位">
                <Input placeholder="请输入期望职位" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expected_salary" label="期望薪资">
                <Input placeholder="请输入期望薪资" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                name="source_id" 
                label="线索来源"
                rules={[{ required: true, message: '请选择线索来源' }]}
              >
                <Select placeholder="请选择来源">
                  {sources && sources.map(source => (
                    <Option key={source.id} value={source.id}>{source.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="province" label="所在省份">
                <Input placeholder="请输入所在省份" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="city" label="所在城市">
                <Input placeholder="请输入所在城市" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="状态">
                <Select placeholder="请选择状态" allowClear>
                  <Option value="non_employee">非员工</Option>
                  <Option value="pending">待面试</Option>
                  <Option value="active">在职中</Option>
                  <Option value="inactive">已离职</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="备注">
            <TextArea rows={4} placeholder="请输入备注信息" />
          </Form.Item>

          <Form.Item label="附件上传">
            <AttachmentUpload leadId={null} />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit">{editingLead ? '更新' : '创建'}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      {/* 线索详情抽屉 */}
      <Drawer
        title="线索详情"
        placement="right"
        width={800}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {currentLead && (
          <>
            {/* 改为纵向分块展示 */}
                <Card size="small" title="基本信息" style={{ marginBottom: 16 }}>
              <Row gutter={[16, 15]}>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>姓名：</strong>{currentLead.name} <Text type="secondary">(线索ID: {currentLead.id})</Text></div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>手机：</strong>
                  <span style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => copyToClipboard(currentLead.phone, '手机号')}>
                          {currentLead.phone}
                        </span>
                </div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>邮箱：</strong>{currentLead.email || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>微信：</strong>{currentLead.wechat ? (
                  <span style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => copyToClipboard(currentLead.wechat, '微信号')}>
                            {currentLead.wechat}
                          </span>
                ) : '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>性别：</strong>{currentLead.gender === 'male' ? '男' : currentLead.gender === 'female' ? '女' : '未知'}</div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>年龄：</strong>{currentLead.age || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>学历：</strong>{currentLead.education || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>工作经验：</strong>{currentLead.experience || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>期望职位：</strong>{currentLead.expected_position || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>期望薪资：</strong>{currentLead.expected_salary || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>来源：</strong>{currentLead.source_name || currentLead.source || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>评级：</strong>{currentLead.rating_name || '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>添加时间：</strong>{currentLead.created_at ? dayjs(currentLead.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</div></Col>
                <Col span={12}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>状态：</strong><Tag color={getStatusColor(currentLead.status)}>{getStatusText(currentLead.status)}</Tag></div></Col>
                <Col span={24}><div style={{ lineHeight: '15px', marginBottom: 0 }}><strong>备注：</strong>{currentLead.notes || '-'}</div></Col>
                  </Row>
              {/* 附件展示 */}
              <Divider style={{ margin: '12px 0' }} />
              <Title level={5} style={{ marginTop: 0 }}>附件</Title>
              {attachments.length === 0 ? (
                <Text type="secondary">暂无附件</Text>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12 }}>
                  {attachments.map((f) => (
                    <div key={f.id} style={{ textAlign: 'center' }}>
                      {f.isImage ? (
                        <img src={f.url} alt={f.originalName} style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }} />
                      ) : (
                        <div style={{ width: 110, height: 110, border: '1px solid #eee', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
                          <FileTextOutlined style={{ fontSize: 28, color: '#999' }} />
                        </div>
                      )}
                      <div style={{ marginTop: 6, fontSize: 12, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.originalName}>{f.originalName}</div>
                      <a href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>查看/下载</a>
                    </div>
                  ))}
                </div>
              )}
                </Card>

                {/* 面试记录 */}
                <Card size="small" title="面试记录" style={{ marginBottom: 16 }}>
                  <InterviewRecords leadId={currentLead.id} leadName={currentLead.name} leadPhone={currentLead.phone} />
                </Card>

                {/* 入职记录 */}
                <Card size="small" title="入职记录" style={{ marginBottom: 16 }}>
                  <OnboardingRecords leadId={currentLead.id} leadName={currentLead.name} leadPhone={currentLead.phone} />
                </Card>

                <Card 
                  size="small" 
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>跟进历史</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text style={{ fontSize: '12px', color: '#666' }}>显示系统操作</Text>
                    <Switch size="small" checked={showSystemOperations} onChange={setShowSystemOperations} />
                    <Button type="text" icon={<ReloadOutlined />} size="small" onClick={() => currentLead && handleView(currentLead)} title="刷新跟进历史" />
                      </div>
                    </div>
                  } 
              style={{ maxHeight: 420, overflow: 'auto' }}
                >
                  <List
                    dataSource={followUpHistory.filter(item => {
                      const c = item.content || '';
                      if (c.includes('【系统操作】线索已认领')) return false;
                      return showSystemOperations || !c.includes('【系统操作】');
                    })}
                    renderItem={(item) => (
                      <List.Item>
                        <div style={{ width: '100%' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            marginBottom: 4,
                        fontSize: 12, 
                            color: '#666'
                          }}>
                            <span>{item.created_by_name || item.created_by_username}</span>
                            <span>{new Date(item.created_at).toLocaleString('zh-CN', { 
                              year: 'numeric', 
                              month: '2-digit', 
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}</span>
                          </div>
                          <div style={{ 
                            backgroundColor: item.content.includes('【系统操作】') ? '#fff2e8' : '#f5f5f5', 
                        padding: 12, 
                        borderRadius: 6,
                        fontSize: 13,
                        lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                        wordBreak: 'word-break',
                            border: item.content.includes('【系统操作】') ? '1px solid #ffd591' : 'none'
                          }}>
                        {renderFollowUpContent(item.content)}
                          </div>
                        </div>
                      </List.Item>
                    )}
                  />
                </Card>
          </>
        )}
      </Drawer>

      {/* 图片查看器 */}
      <ImageViewer
        visible={imageViewerVisible}
        onClose={closeImageViewer}
        imageUrl={currentImage.url}
        imageName={currentImage.name}
        downloadUrl={currentImage.downloadUrl}
      />
    </div>
    </main>
  );
};

// 获取状态颜色
const getStatusColor = (status) => {
  const statusColors = {
    'new': 'blue',
    'contacted': 'orange',
    'qualified': 'green',
    'unqualified': 'red',
    'converted': 'purple',
    'non_employee': 'default',
    'pending': 'processing',
    'interviewed': 'cyan',
    'offered': 'gold',
    'hired': 'success',
    'rejected': 'error',
    'onboarding': 'processing'
  };
  return statusColors[status] || 'default';
};

// 获取状态文本
const getStatusText = (status) => {
  const statusTexts = {
    'new': '新线索',
    'contacted': '已联系',
    'qualified': '合格',
    'unqualified': '不合格',
    'converted': '已转化',
    'non_employee': '非员工',
    'pending': '待面试',
    'interviewed': '已面试',
    'offered': '已发offer',
    'hired': '已入职',
    'rejected': '已拒绝',
    'onboarding': '入职中'
  };
  return statusTexts[status] || status || '-';
};

export default PublicLeads; 